/**
 * Settings → Your profile. Lets the signed-in user edit their own
 * displayName (mirrored to all member docs by fan_out_user_profile)
 * and start an email-change flow.
 *
 * This sits *above* the elder-profile Card on the Settings page —
 * "your profile" applies to the caregiver, "the elder's profile"
 * applies to the account.
 */

import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { hasPasswordProvider, startEmailChange } from '@/lib/auth';
import { updateUserProfile } from '@/lib/firestore';

export function YourProfileSection() {
  const auth = useAuth();
  const uid = auth.state === 'signedIn' ? auth.user.uid : null;
  const profile = useUserProfile(uid);

  const [displayName, setDisplayName] = useState('');
  const [savedDisplayName, setSavedDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  // Hydrate from the profile doc once. useUserProfile re-emits a fresh object
  // per snapshot; without this guard a late emission would overwrite an
  // in-progress edit and reset savedDisplayName (which drives the dirty flag).
  const hydrated = useRef(false);
  useEffect(() => {
    if (profile.state === 'present' && !hydrated.current) {
      hydrated.current = true;
      const next = profile.profile.displayName ?? '';
      setDisplayName(next);
      setSavedDisplayName(next);
    }
  }, [profile]);

  if (auth.state !== 'signedIn') return null;
  const user = auth.user;
  const dirty = displayName.trim() !== savedDisplayName.trim();

  async function onSaveDisplayName() {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const next = displayName.trim();
      await updateUserProfile({ displayName: next });
      setSavedDisplayName(next);
      setInfo('Saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Field label="Display name" htmlFor="your-display-name" hint="Other caregivers see this on the Caregivers list.">
        <div className="flex gap-2">
          <Input
            id="your-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Michael"
            maxLength={100}
          />
          <Button onClick={() => void onSaveDisplayName()} disabled={busy || !dirty}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Field>

      <div>
        <span className="block text-sm font-medium text-ink">Email</span>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-sm text-ink-soft">{user.email ?? '(no email)'}</span>
          <Button size="sm" variant="ghost" onClick={() => setEmailDialogOpen(true)}>
            Change email
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
      {info && (
        <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{info}</div>
      )}

      <ChangeEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        currentEmail={user.email ?? ''}
        canPasswordReauth={hasPasswordProvider(user)}
      />
    </div>
  );
}

function ChangeEmailDialog({
  open,
  onOpenChange,
  currentEmail,
  canPasswordReauth,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentEmail: string;
  canPasswordReauth: boolean;
}) {
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setNewEmail('');
      setCurrentPassword('');
      setError(null);
      setDone(false);
    }
  }, [open]);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!newEmail || newEmail === currentEmail) {
      setError('Enter a different email.');
      return;
    }
    if (canPasswordReauth && !currentPassword) {
      setError('Current password is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await startEmailChange(
        newEmail.trim(),
        canPasswordReauth ? currentPassword : undefined,
      );
      // Optimistically update the /users/{uid}.email so the caregiver
      // list and member docs reflect the new address before the user
      // verifies. Firebase Auth's auth.email won't update until the
      // verification link is clicked; the brief inconsistency is OK.
      await updateUserProfile({ email: newEmail.trim() });
      setDone(true);
    } catch (e) {
      setError(humanAuthError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogTitle>Change email</DialogTitle>
        <DialogDescription className="mt-1">
          {done
            ? `Verification link sent to ${newEmail}. Click it to confirm.`
            : `We'll send a verification link to your new email. Click it to confirm the change. You'll keep using ${currentEmail || 'your current email'} to sign in until you verify.`}
        </DialogDescription>
        {!done ? (
          <form className="mt-4 flex flex-col gap-3" onSubmit={onSubmit}>
            <Field label="New email" htmlFor="new-email">
              <Input
                id="new-email"
                type="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </Field>
            {canPasswordReauth ? (
              <Field
                label="Current password"
                htmlFor="current-password"
                hint="Confirms it's you — Firebase requires a recent sign-in for sensitive changes."
              >
                <Input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </Field>
            ) : (
              <p className="text-xs text-ink-soft">
                You signed in with Google — when you submit, a Google popup will
                ask you to confirm.
              </p>
            )}
            {error && (
              <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? 'Sending…' : 'Send verification'}
              </Button>
            </div>
          </form>
        ) : (
          <div className="mt-4 flex justify-end">
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function humanAuthError(e: unknown): string {
  if (e instanceof Error) {
    const msg = e.message;
    if (msg.includes('auth/wrong-password') || msg.includes('auth/invalid-credential')) {
      return 'Current password is wrong.';
    }
    if (msg.includes('auth/email-already-in-use')) {
      return 'That email is already used by another account.';
    }
    if (msg.includes('auth/requires-recent-login')) {
      return 'Please sign out and back in, then try again.';
    }
    return msg.replace(/^FirebaseError:\s*/, '');
  }
  return 'Could not change email.';
}
